/**
 * Square payroll sync contracts (WP4-TIMECLOCK-SQUARE). Routes under
 * `/v1/timeclock-square`.
 *
 * Velora's timeclock stays the place staff punch. Each Velora shift (one
 * person's entries at one office joined by breaks) becomes one Square Labor
 * timecard with its breaks, at the Square location linked to the office and
 * for the Square team member linked to the person. Square Payroll pays from
 * those timecards; Velora never runs payroll. Edits made in Square come back
 * (webhooks and a periodic read) and are applied when Velora has not changed
 * the same shift, or flagged as a conflict for a manager when both sides did.
 *
 * Square has no public API that runs payroll or reads payroll runs, so the
 * payroll page is a preview of the hours Square will pay from.
 */
import { z } from 'zod';
import { PersonKeySchema } from './timeclock.js';

/** SSE entity key; every query key of the Square screens starts with it. */
export const TIMECLOCK_SQUARE_ENTITY = 'timeclock_square';

export const SQUARE_PAYROLL_PERMISSIONS = {
  /** Mapping, sync status, retry, conflicts, payroll preview. */
  manage: 'timeclock.manage',
  /** Connecting and disconnecting the Square account. */
  connect: 'settings.manage',
} as const;

/** OAuth scopes Velora asks for (https://developer.squareup.com/docs/oauth-api/square-permissions). */
export const SQUARE_PAYROLL_SCOPES = [
  'MERCHANT_PROFILE_READ',
  'EMPLOYEES_READ',
  'TIMECARDS_READ',
  'TIMECARDS_WRITE',
  'TIMECARDS_SETTINGS_READ',
  'TIMECARDS_SETTINGS_WRITE',
] as const;

/** A break shorter than this gap still belongs to the same shift. */
export const SHIFT_BREAK_MAX_HOURS = 4;

export const SquareEnvironmentSchema = z.enum(['sandbox', 'production']);
export type SquareEnvironment = z.infer<typeof SquareEnvironmentSchema>;

export const WeekdaySchema = z.enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);
export type Weekday = z.infer<typeof WeekdaySchema>;

export const PayFrequencySchema = z.enum(['weekly', 'biweekly', 'semimonthly', 'monthly']);
export type PayFrequency = z.infer<typeof PayFrequencySchema>;

const IsoDate = z.iso.date();
const Hhmm = z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/);

export const OvertimeRulesSchema = z.object({
  /** Weekly overtime after this many hours (null: no weekly overtime). */
  weeklyOvertimeHours: z.number().min(1).max(168).nullable(),
  /** Daily overtime after this many hours in one workday (null: none). */
  dailyOvertimeHours: z.number().min(1).max(24).nullable(),
  /** Daily double time after this many hours in one workday (null: none). */
  dailyDoubleTimeHours: z.number().min(1).max(24).nullable(),
});
export type OvertimeRules = z.infer<typeof OvertimeRulesSchema>;

/* ------------------------------------------------------------------------ */
/* Connection                                                                */
/* ------------------------------------------------------------------------ */

export const SquareModeSchema = z.object({
  environment: SquareEnvironmentSchema,
  /**
   * `simulated`: VELORA_SQUARE_PAYROLL_LIVE is off on this server, so every
   * call goes to Velora's built-in Square simulator and nothing leaves Velora.
   */
  simulated: z.boolean(),
  /** The server has a Square application configured for OAuth. */
  oauthAvailable: z.boolean(),
  /** The server can verify Square webhooks (signature key and URL set). */
  webhooksConfigured: z.boolean(),
});
export type SquareMode = z.infer<typeof SquareModeSchema>;

export const SquareConnectionSchema = z.object({
  mode: SquareModeSchema,
  connection: z
    .object({
      authMode: z.enum(['oauth', 'token']),
      environment: SquareEnvironmentSchema,
      merchantId: z.string().nullable(),
      merchantName: z.string().nullable(),
      status: z.enum(['connected', 'needs_reauth', 'disconnected']),
      tokenHint: z.string().nullable(),
      tokenExpiresAt: z.string().nullable(),
      scopes: z.array(z.string()),
      syncEnabled: z.boolean(),
      syncFrom: IsoDate.nullable(),
      workweekStart: WeekdaySchema,
      workdayStart: Hhmm,
      workweekSyncedAt: z.string().nullable(),
      overtime: OvertimeRulesSchema,
      payFrequency: PayFrequencySchema,
      payPeriodAnchor: IsoDate.nullable(),
      lastSyncAt: z.string().nullable(),
      lastSyncError: z.string().nullable(),
      lastPullAt: z.string().nullable(),
      connectedAt: z.string(),
    })
    .nullable(),
});
export type SquareConnection = z.infer<typeof SquareConnectionSchema>;

/** Body for POST /connect/token: a SANDBOX personal access token, for testing. */
export const ConnectTokenBody = z.object({
  accessToken: z.string().trim().min(16).max(512),
});
export type ConnectTokenBody = z.infer<typeof ConnectTokenBody>;

export const OAuthStartSchema = z.object({ url: z.url() });
export type OAuthStart = z.infer<typeof OAuthStartSchema>;

export const OAuthCompleteBody = z.object({
  code: z.string().min(1).max(512),
  state: z.string().min(1).max(2048),
});
export type OAuthCompleteBody = z.infer<typeof OAuthCompleteBody>;

export const UpdateSettingsBody = z
  .object({
    syncEnabled: z.boolean().optional(),
    syncFrom: IsoDate.nullable().optional(),
    overtime: OvertimeRulesSchema.optional(),
    payFrequency: PayFrequencySchema.optional(),
    payPeriodAnchor: IsoDate.nullable().optional(),
  })
  .refine(
    (b) =>
      !b.overtime ||
      b.overtime.dailyDoubleTimeHours === null ||
      b.overtime.dailyOvertimeHours === null ||
      b.overtime.dailyDoubleTimeHours > b.overtime.dailyOvertimeHours,
    { message: 'Double time must start after daily overtime.' },
  );
export type UpdateSettingsBody = z.infer<typeof UpdateSettingsBody>;

/* ------------------------------------------------------------------------ */
/* Offices and staff mapping                                                 */
/* ------------------------------------------------------------------------ */

export const SquareLocationSchema = z.object({
  id: z.string(),
  name: z.string(),
  timezone: z.string().nullable(),
  active: z.boolean(),
});
export type SquareLocation = z.infer<typeof SquareLocationSchema>;

export const OfficeLinkSchema = z.object({
  locationId: z.uuid(),
  officeName: z.string(),
  officeTimezone: z.string(),
  squareLocationId: z.string().nullable(),
  squareLocationName: z.string().nullable(),
  squareTimezone: z.string().nullable(),
  breakTypeName: z.string().nullable(),
  /** The Square location's time zone differs from the office's. */
  timezoneMismatch: z.boolean(),
});
export type OfficeLink = z.infer<typeof OfficeLinkSchema>;

export const OfficeMappingSchema = z.object({
  offices: z.array(OfficeLinkSchema),
  squareLocations: z.array(SquareLocationSchema),
});
export type OfficeMapping = z.infer<typeof OfficeMappingSchema>;

export const LinkOfficeBody = z.object({
  locationId: z.uuid(),
  /** null unlinks the office. */
  squareLocationId: z.string().min(1).max(64).nullable(),
});
export type LinkOfficeBody = z.infer<typeof LinkOfficeBody>;

export const JobAssignmentSchema = z.object({
  jobId: z.string().nullable(),
  title: z.string(),
  payType: z.enum(['HOURLY', 'SALARY', 'NONE']),
  hourlyRateCents: z.number().int().nullable(),
  currency: z.string().nullable(),
});
export type JobAssignment = z.infer<typeof JobAssignmentSchema>;

export const SquareTeamMemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
  isOwner: z.boolean(),
  jobs: z.array(JobAssignmentSchema),
  overtimeExempt: z.boolean(),
  /** Velora user already linked to this team member. */
  /** Timeclock person already linked to this team member (u:<user> or s:<PIN-only staff>). */
  linkedPersonKey: PersonKeySchema.nullable(),
});
export type SquareTeamMember = z.infer<typeof SquareTeamMemberSchema>;

export const MatchReasonSchema = z.enum(['email', 'name', 'manual']);
export type MatchReason = z.infer<typeof MatchReasonSchema>;

export const MatchSuggestionSchema = z.object({
  teamMemberId: z.string(),
  reason: z.enum(['email', 'name', 'similar_name']),
  /** 0..1; email = 1, exact full name 0.9, similar names lower. */
  score: z.number().min(0).max(1),
});
export type MatchSuggestion = z.infer<typeof MatchSuggestionSchema>;

export const StaffLinkSchema = z.object({
  /** u:<Velora user> or s:<PIN-only staff> (timeclock people, as on the timesheet). */
  personKey: PersonKeySchema,
  /** False for PIN-only staff (no Velora login). */
  hasLogin: z.boolean(),
  name: z.string(),
  email: z.string().nullable(),
  link: z
    .object({
      teamMemberId: z.string(),
      teamMemberName: z.string(),
      jobId: z.string().nullable(),
      jobTitle: z.string().nullable(),
      matchedBy: MatchReasonSchema,
      linkedAt: z.string(),
      /** The team member is gone or inactive in Square. */
      stale: z.boolean(),
    })
    .nullable(),
  suggestions: z.array(MatchSuggestionSchema),
});
export type StaffLink = z.infer<typeof StaffLinkSchema>;

export const StaffMappingSchema = z.object({
  staff: z.array(StaffLinkSchema),
  teamMembers: z.array(SquareTeamMemberSchema),
  teamFetchedAt: z.string().nullable(),
});
export type StaffMapping = z.infer<typeof StaffMappingSchema>;

export const LinkStaffBody = z.object({
  personKey: PersonKeySchema,
  /** null unlinks. */
  teamMemberId: z.string().min(1).max(64).nullable(),
  jobId: z.string().min(1).max(64).nullable().optional(),
  matchedBy: MatchReasonSchema.optional(),
});
export type LinkStaffBody = z.infer<typeof LinkStaffBody>;

/** Body for POST /staff/accept-suggestions: link every best email or exact-name match. */
export const AcceptSuggestionsBody = z.object({
  personKeys: z.array(PersonKeySchema).min(1).max(500),
});
export type AcceptSuggestionsBody = z.infer<typeof AcceptSuggestionsBody>;

/* ------------------------------------------------------------------------ */
/* Sync status                                                               */
/* ------------------------------------------------------------------------ */

export const TimecardSyncStatusSchema = z.enum([
  'pending',
  'synced',
  'error',
  'conflict',
  'remote_deleted',
  'superseded',
  'skipped',
]);
export type TimecardSyncStatus = z.infer<typeof TimecardSyncStatusSchema>;

/** What the timesheet shows for one entry. */
export const EntrySyncStateSchema = z.enum([
  ...TimecardSyncStatusSchema.options,
  /** Still clocked in or on a break: pushed once the shift ends. */
  'open',
  /** The person has no Square team member yet. */
  'unmapped',
  /** Before the practice's "sync from" date. */
  'before_start',
  /** The office has no Square location. */
  'office_unlinked',
  /** Sync is off for the practice. */
  'off',
]);
export type EntrySyncState = z.infer<typeof EntrySyncStateSchema>;

export const ShiftTimesSchema = z.object({
  start: z.string(),
  end: z.string().nullable(),
  breaks: z.array(z.object({ start: z.string(), end: z.string().nullable() })),
});
export type ShiftTimes = z.infer<typeof ShiftTimesSchema>;

export const EntrySyncSchema = z.object({
  entryId: z.uuid(),
  state: EntrySyncStateSchema,
  /** The sync row (null for computed states such as `unmapped`). */
  timecardSyncId: z.uuid().nullable(),
  squareTimecardId: z.string().nullable(),
  detail: z.string().nullable(),
  syncedAt: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
});
export type EntrySync = z.infer<typeof EntrySyncSchema>;

export const SyncStatusQuerySchema = z.object({ from: IsoDate, to: IsoDate });

export const SyncStatusSchema = z.object({
  connected: z.boolean(),
  syncEnabled: z.boolean(),
  simulated: z.boolean(),
  entries: z.array(EntrySyncSchema),
  counts: z.record(z.string(), z.number().int().nonnegative()),
  lastSyncAt: z.string().nullable(),
});
export type SyncStatus = z.infer<typeof SyncStatusSchema>;

export const ConflictSchema = z.object({
  id: z.uuid(),
  personKey: PersonKeySchema,
  name: z.string(),
  squareTimecardId: z.string().nullable(),
  velora: ShiftTimesSchema,
  square: ShiftTimesSchema.nullable(),
  lastSynced: ShiftTimesSchema.nullable(),
  detail: z.string().nullable(),
});
export type Conflict = z.infer<typeof ConflictSchema>;

export const ConflictListSchema = z.object({ conflicts: z.array(ConflictSchema) });
export type ConflictList = z.infer<typeof ConflictListSchema>;

export const ResolveConflictBody = z.object({
  /** `velora`: overwrite Square with Velora's times. `square`: apply Square's times to Velora. */
  keep: z.enum(['velora', 'square']),
  reason: z.string().trim().min(3).max(500),
});
export type ResolveConflictBody = z.infer<typeof ResolveConflictBody>;

export const SyncRunResultSchema = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  pulled: z.number().int().nonnegative(),
  conflicts: z.number().int().nonnegative(),
  errors: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
});
export type SyncRunResult = z.infer<typeof SyncRunResultSchema>;

/* ------------------------------------------------------------------------ */
/* Payroll preview                                                           */
/* ------------------------------------------------------------------------ */

export const PayrollQuerySchema = z.object({
  /** Any date inside the pay period; defaults to today. */
  date: IsoDate.optional(),
});

export const HoursByTypeSchema = z.object({
  regularMinutes: z.number().int().nonnegative(),
  overtimeMinutes: z.number().int().nonnegative(),
  doubleTimeMinutes: z.number().int().nonnegative(),
  unpaidBreakMinutes: z.number().int().nonnegative(),
});
export type HoursByType = z.infer<typeof HoursByTypeSchema>;

export const PayrollWeekSchema = HoursByTypeSchema.extend({ weekStart: IsoDate });
export type PayrollWeek = z.infer<typeof PayrollWeekSchema>;

export const PayrollPersonSchema = z.object({
  personKey: PersonKeySchema.nullable(),
  name: z.string(),
  teamMemberId: z.string().nullable(),
  jobTitle: z.string().nullable(),
  overtimeExempt: z.boolean(),
  /** From the timecards in Square: what Square Payroll pays from. */
  square: HoursByTypeSchema,
  /** From Velora's entries for the same period. */
  velora: HoursByTypeSchema,
  weeks: z.array(PayrollWeekSchema),
  timecards: z.number().int().nonnegative(),
  /** Square and Velora disagree by a minute or more. */
  mismatch: z.boolean(),
  /** Shifts not in Square yet (unsynced, errors, conflicts). */
  notInSquare: z.number().int().nonnegative(),
});
export type PayrollPerson = z.infer<typeof PayrollPersonSchema>;

export const PayrollPreviewSchema = z.object({
  periodStart: IsoDate,
  periodEnd: IsoDate,
  payFrequency: PayFrequencySchema,
  workweekStart: WeekdaySchema,
  workdayStart: Hhmm,
  overtime: OvertimeRulesSchema,
  generatedAt: z.string(),
  simulated: z.boolean(),
  people: z.array(PayrollPersonSchema),
  totals: HoursByTypeSchema,
  /** Velora's timesheet weeks start on Sunday; Square's may not. */
  workweekDiffers: z.boolean(),
  warnings: z.array(z.string()),
});
export type PayrollPreview = z.infer<typeof PayrollPreviewSchema>;

/* ------------------------------------------------------------------------ */
/* Webhooks                                                                  */
/* ------------------------------------------------------------------------ */

/** Event types Velora subscribes to (Developer Console > Webhooks). */
export const SQUARE_WEBHOOK_EVENTS = [
  'labor.timecard.created',
  'labor.timecard.updated',
  'labor.timecard.deleted',
  'team_member.created',
  'team_member.updated',
  'team_member.wage_setting.updated',
] as const;

export const SquareWebhookAckSchema = z.object({
  received: z.boolean(),
  duplicate: z.boolean(),
  outcome: z.string(),
});
export type SquareWebhookAck = z.infer<typeof SquareWebhookAckSchema>;
